document.addEventListener('DOMContentLoaded', () => {
  const modal = document.getElementById('MMEQuickView');
  const content = modal?.querySelector('.mme-quick-view__content');

  if (!modal || !content) return;

  let currentProduct = null;
  let selectedVariantId = null;
  let currentProductIsFinalSale = false;
  let currentProductIsInStoreOnly = false;
  let currentProductTemplate = '';

  const formatMoney = cents => {
    return new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }
    ).format(cents / 100);
  };

  const escapeHtml = value => {
    const element = document.createElement('div');
    element.textContent = value || '';
    return element.innerHTML;
  };

  const openModal = () => {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mme-quick-view-open');
  };

  const closeModal = () => {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mme-quick-view-open');

    selectedVariantId = null;
    currentProductIsFinalSale = false;
    currentProductIsInStoreOnly = false;
    currentProductTemplate = '';
  };

  const showLoading = () => {
    content.innerHTML = `
      <div class="mme-quick-view__loading">
        Loading product…
      </div>
    `;
  };

  const showError = message => {
    content.innerHTML = `
      <div class="mme-quick-view__loading">
        ${escapeHtml(
          message || "We couldn't load this product. Please try again."
        )}
      </div>
    `;
  };

  const getProductTags = product => {
    if (Array.isArray(product.tags)) {
      return product.tags;
    }

    if (typeof product.tags === 'string') {
      return product.tags.split(',');
    }

    return [];
  };

  const isFinalSaleProduct = product => {
    return getProductTags(product).some(tag => {
      return tag.trim().toLowerCase() === 'final sale';
    });
  };

  const hasInStoreOnlyTag = product => {
    return getProductTags(product).some(tag => {
      return tag.trim().toLowerCase() === 'in store only';
    });
  };

  const isInStoreOnlyProduct = product => {
    const normalizedTemplate = currentProductTemplate
      .trim()
      .toLowerCase()
      .replace(/^product\./, '');

    const templateIsInStore =
      normalizedTemplate === 'in-store';

    return templateIsInStore || hasInStoreOnlyTag(product);
  };

  const getPriceMarkup = product => {
    const price = Number(product.price || 0);
    const compareAtPrice = Number(product.compare_at_price || 0);

    if (compareAtPrice > price) {
      return `
        <div class="mme-quick-view__sale-price">
          <s class="mme-quick-view__compare-price">
            ${formatMoney(compareAtPrice)}
          </s>

          <span class="mme-quick-view__current-price">
            ${formatMoney(price)}
          </span>

          <span class="mme-quick-view__sale-badge">
            Sale
          </span>
        </div>
      `;
    }

    return `
      <div class="mme-quick-view__regular-price">
        ${formatMoney(price)}
      </div>
    `;
  };

  const updateAddButtonState = () => {
    const addButton = modal.querySelector(
      '.mme-quick-view__add-button'
    );

    if (!addButton) return;

    if (currentProductIsInStoreOnly) {
      addButton.disabled = true;
      addButton.textContent = 'In Store Only';
      return;
    }

    if (!selectedVariantId) {
      addButton.disabled = true;
      addButton.textContent = 'Select a Size';
      return;
    }

    if (currentProductIsFinalSale) {
      const finalSaleCheckbox = modal.querySelector(
        '.mme-quick-view__final-sale-checkbox'
      );

      if (!finalSaleCheckbox?.checked) {
        addButton.disabled = true;
        addButton.textContent = 'Acknowledge Final Sale';
        return;
      }
    }

    addButton.disabled = false;
    addButton.textContent = 'Add to Cart';
  };

  const renderProduct = product => {
    currentProduct = product;
    selectedVariantId = null;

    currentProductIsFinalSale =
      isFinalSaleProduct(product);

    currentProductIsInStoreOnly =
      isInStoreOnlyProduct(product);

    const productImages = Array.isArray(product.images)
      ? product.images
      : [];

    const mainImage =
      product.featured_image ||
      productImages[0] ||
      '';

    const thumbnails = productImages
      .map((image, index) => {
        return `
          <button
            type="button"
            class="mme-quick-view__thumbnail${
              index === 0 ? ' is-active' : ''
            }"
            data-quick-view-image="${escapeHtml(image)}"
            aria-label="View image ${index + 1}"
          >
            <img
              src="${escapeHtml(image)}"
              alt=""
              loading="lazy"
            >
          </button>
        `;
      })
      .join('');

    const sizeButtons = product.variants
      .map(variant => {
        const unavailableClass = variant.available
          ? ''
          : ' is-unavailable';

        return `
          <button
            type="button"
            class="mme-quick-view__size${unavailableClass}"
            data-variant-id="${variant.id}"
            ${variant.available ? '' : 'disabled'}
          >
            ${escapeHtml(variant.title)}
          </button>
        `;
      })
      .join('');

    const finalSaleMarkup = currentProductIsFinalSale
      ? `
        <div class="mme-quick-view__final-sale">
          <div class="mme-quick-view__final-sale-heading">
            FINAL SALE
          </div>

          <div class="mme-quick-view__final-sale-text">
            Final sale items cannot be returned or exchanged.
          </div>

          <label class="mme-quick-view__final-sale-label">
            <input
              type="checkbox"
              class="mme-quick-view__final-sale-checkbox"
            >

            <span>
              I acknowledge that this item cannot be returned or exchanged.
            </span>
          </label>
        </div>
      `
      : '';

    const inStoreOnlyMarkup = currentProductIsInStoreOnly
      ? `
        <div class="mme-quick-view__final-sale-notice">
          IN STORE ONLY
        </div>
      `
      : '';

    content.innerHTML = `
      <div class="mme-quick-view__product">

        <div class="mme-quick-view__image-column">
          ${
            mainImage
              ? `
                <div class="mme-quick-view__main-image-wrap">

                  ${
                    productImages.length > 1
                      ? `
                        <button
                          type="button"
                          class="mme-quick-view__image-arrow mme-quick-view__image-arrow--previous"
                          data-quick-view-image-previous
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                      `
                      : ''
                  }

                  <img
                    class="mme-quick-view__image"
                    src="${escapeHtml(mainImage)}"
                    alt="${escapeHtml(product.title)}"
                  >

                  ${
                    productImages.length > 1
                      ? `
                        <button
                          type="button"
                          class="mme-quick-view__image-arrow mme-quick-view__image-arrow--next"
                          data-quick-view-image-next
                          aria-label="Next image"
                        >
                          ›
                        </button>
                      `
                      : ''
                  }

                </div>

                ${
                  productImages.length > 1
                    ? `
                      <div class="mme-quick-view__thumbnails">
                        ${thumbnails}
                      </div>
                    `
                    : ''
                }
              `
              : ''
          }
        </div>

        <div class="mme-quick-view__details">

          ${
            product.vendor
              ? `
                <div class="mme-quick-view__vendor">
                  ${escapeHtml(product.vendor)}
                </div>
              `
              : ''
          }

          <h2 class="mme-quick-view__title">
            ${escapeHtml(product.title)}
          </h2>

          <div class="mme-quick-view__price">
            ${getPriceMarkup(product)}
          </div>

          <div class="mme-quick-view__description">
            ${product.description || ''}
          </div>

          ${
            currentProductIsFinalSale
              ? `
                <div class="mme-quick-view__final-sale-notice">
                  FINAL SALE
                </div>
              `
              : ''
          }

          ${inStoreOnlyMarkup}

          <div class="mme-quick-view__size-heading">
            Select Size
          </div>

          <div class="mme-quick-view__sizes">
            ${sizeButtons}
          </div>

          ${finalSaleMarkup}

          <button
            type="button"
            class="mme-quick-view__add-button"
            disabled
          >
            ${
              currentProductIsInStoreOnly
                ? 'In Store Only'
                : 'Select a Size'
            }
          </button>

          <div
            class="mme-quick-view__message"
            role="status"
            aria-live="polite"
          ></div>

          <a
            class="mme-quick-view__details-link"
            href="${escapeHtml(product.url)}"
          >
            View Full Details
          </a>

        </div>
      </div>
    `;

    updateAddButtonState();
  };

  const loadProduct = async handle => {
    showLoading();

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${root}products/${handle}.js`
      );

      if (!response.ok) {
        throw new Error(
          `Product request failed: ${response.status}`
        );
      }

      const product = await response.json();

      renderProduct(product);

    } catch (error) {
      console.error(
        'Quick View product error:',
        error
      );

      showError();
    }
  };

  const changeMainImage = thumbnail => {
    const mainImage = modal.querySelector(
      '.mme-quick-view__image'
    );

    const imageUrl =
      thumbnail.dataset.quickViewImage;

    if (!mainImage || !imageUrl) return;

    mainImage.src = imageUrl;

    modal
      .querySelectorAll(
        '.mme-quick-view__thumbnail'
      )
      .forEach(button => {
        button.classList.remove('is-active');
      });

    thumbnail.classList.add('is-active');
  };

  const moveGallery = direction => {
    const thumbnails = Array.from(
      modal.querySelectorAll(
        '.mme-quick-view__thumbnail'
      )
    );

    if (!thumbnails.length) return;

    let activeIndex =
      thumbnails.findIndex(button => {
        return button.classList.contains(
          'is-active'
        );
      });

    if (activeIndex < 0) activeIndex = 0;

    const nextIndex =
      direction === 'previous'
        ? (
            activeIndex -
            1 +
            thumbnails.length
          ) % thumbnails.length
        : (
            activeIndex +
            1
          ) % thumbnails.length;

    changeMainImage(
      thumbnails[nextIndex]
    );

    thumbnails[nextIndex].scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest'
    });
  };

  const selectVariant = sizeButton => {
    if (sizeButton.disabled) return;

    if (currentProductIsInStoreOnly) {
      updateAddButtonState();
      return;
    }

    selectedVariantId = Number(
      sizeButton.dataset.variantId
    );

    modal
      .querySelectorAll(
        '.mme-quick-view__size'
      )
      .forEach(button => {
        button.classList.remove(
          'is-selected'
        );
      });

    sizeButton.classList.add(
      'is-selected'
    );

    updateAddButtonState();
  };

  const addToCart = async addButton => {

    if (currentProductIsInStoreOnly) {
      addButton.disabled = true;
      addButton.textContent =
        'In Store Only';

      return;
    }

    if (!selectedVariantId) {
      updateAddButtonState();
      return;
    }

    if (currentProductIsFinalSale) {
      const finalSaleCheckbox =
        modal.querySelector(
          '.mme-quick-view__final-sale-checkbox'
        );

      if (!finalSaleCheckbox?.checked) {
        updateAddButtonState();
        return;
      }
    }

    const message =
      modal.querySelector(
        '.mme-quick-view__message'
      );

    addButton.disabled = true;
    addButton.textContent = 'Adding…';

    if (message) {
      message.textContent = '';
    }

    const cartNotification =
      document.querySelector(
        'cart-notification'
      );

    const sections = cartNotification
      ? cartNotification
          .getSectionsToRender()
          .map(section => section.id)
      : [];

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
            Accept: 'application/json'
          },

          body: JSON.stringify({
            id: selectedVariantId,
            quantity: 1,
            sections,
            sections_url:
              window.location.pathname
          })
        }
      );

      const parsedState =
        await response.json();

      if (!response.ok) {
        throw new Error(
          parsedState.description ||
          parsedState.message ||
          'Unable to add this item.'
        );
      }

      if (
        cartNotification &&
        parsedState.sections
      ) {
        cartNotification.renderContents(
          parsedState
        );
      }

      addButton.textContent = 'Added';

      closeModal();

    } catch (error) {
      console.error(
        'Quick View add-to-cart error:',
        error
      );

      addButton.disabled = false;
      addButton.textContent =
        'Add to Cart';

      if (message) {
        message.textContent =
          error.message ||
          'Unable to add this item.';
      }
    }
  };

  document.addEventListener(
    'click',
    event => {

      const quickViewButton =
        event.target.closest(
          '.mme-quick-view-button'
        );

      if (quickViewButton) {
        event.preventDefault();
        event.stopPropagation();

        currentProductTemplate =
          quickViewButton.dataset
            .productTemplate || '';

        const normalizedTemplate =
          currentProductTemplate
            .trim()
            .toLowerCase()
            .replace(/^product\./, '');

        currentProductIsInStoreOnly =
          normalizedTemplate === 'in-store';

        openModal();

        loadProduct(
          quickViewButton.dataset
            .productHandle
        );

        return;
      }

      const thumbnail =
        event.target.closest(
          '.mme-quick-view__thumbnail'
        );

      if (thumbnail) {
        event.preventDefault();

        changeMainImage(thumbnail);

        return;
      }

      const previousArrow =
        event.target.closest(
          '[data-quick-view-image-previous]'
        );

      if (previousArrow) {
        event.preventDefault();

        moveGallery('previous');

        return;
      }

      const nextArrow =
        event.target.closest(
          '[data-quick-view-image-next]'
        );

      if (nextArrow) {
        event.preventDefault();

        moveGallery('next');

        return;
      }

      const sizeButton =
        event.target.closest(
          '.mme-quick-view__size'
        );

      if (sizeButton) {
        event.preventDefault();

        selectVariant(sizeButton);

        return;
      }

      const addButton =
        event.target.closest(
          '.mme-quick-view__add-button'
        );

      if (addButton) {
        event.preventDefault();

        addToCart(addButton);

        return;
      }

      const clickedOverlay =
        event.target === modal ||
        event.target.classList.contains(
          'mme-quick-view__overlay'
        );

      const clickedCloseButton =
        event.target.closest(
          '.mme-quick-view__close'
        );

      if (
        clickedOverlay ||
        clickedCloseButton
      ) {
        event.preventDefault();

        closeModal();
      }
    }
  );

  document.addEventListener(
    'change',
    event => {

      if (
        event.target.matches(
          '.mme-quick-view__final-sale-checkbox'
        )
      ) {
        updateAddButtonState();
      }
    }
  );

  document.addEventListener(
    'keydown',
    event => {

      if (
        event.key === 'Escape' &&
        modal.classList.contains(
          'is-open'
        )
      ) {
        closeModal();
      }
    }
  );
});